import { UsersThree, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import type { CSSProperties, ReactNode } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { Icon } from '../atoms/Icon';
import { cardSurface } from '../lib/card';
import { cn } from '../lib/cn';
import { personInk, personSolid } from '../lib/person';
import type { WeekPerson } from './WeekStrip';
import { uiText } from '../i18n';

export type AgendaState = 'past' | 'now' | 'next' | 'later';

export interface AgendaItem {
  id: string;
  start?: string;
  end?: string;
  title: string;
  icon: PhosphorIcon;
  people: 'family' | WeekPerson[];
  state: AgendaState;
  /** e.g. "in 52 Min." – appended to the "Als Nächstes" label. */
  when?: string;
  /** Logistics, what to bring … */
  meta?: ReactNode;
  /** Another person's item while a lens is active – shown quieter, never hidden. */
  dimmed?: boolean;
  /** Small extra lines, e.g. the day's lessons of a school span (1.24). */
  details?: string[];
  /** Free minutes before this item – shown as "1 Std. 30 Min. frei" (1.24). */
  gapBefore?: number;
  /** Title of another item at the same time for the same person (1.24). */
  overlap?: string;
  /** All-day entry kind for the pinned row: school holidays look like a band, birthdays warm. */
  pinTone?: 'holiday' | 'publicHoliday' | 'birthday';
}

export interface AgendaProps {
  items: AgendaItem[];
  /** e.g. "14:32" – rendered as a line before the first item that is not over yet. */
  nowLabel?: string;
  onItemPress?: (id: string) => void;
  /**
   * All-day items as small chips above the plan, sticky while scrolling (Mike, 18.10.). Used by
   * Heute and the calendar's day view alike – one day plan for both.
   */
  pinAllDay?: boolean;
  /** The pinned row sticks below the header (default); lists of several days turn it off. */
  pinSticky?: boolean;
  /** Where the pinned row sticks, e.g. below a docked week strip (CSS length; default the header). */
  pinTop?: string;
  className?: string;
}

const pinTone = {
  holiday: 'bg-feedback-warning-surface text-feedback-warning',
  publicHoliday: 'border-2 border-feedback-danger text-feedback-danger bg-surface-raised',
  birthday: 'bg-accent-tertiary-soft text-text-primary',
} as const;

/** The pinned all-day row – also used alone where only the chips are needed. */
export function PinnedRow({
  items,
  onItemPress,
  className,
  style,
}: {
  items: AgendaItem[];
  onItemPress?: (id: string) => void;
  className?: string;
  style?: CSSProperties;
}) {
  if (!items.length) return null;
  return (
    <div
      role="group"
      style={style}
      aria-label={uiText().allDay}
      className={cn('flex flex-wrap items-center gap-gap-xs', className)}
    >
      {/* visible, so the chips read as all-day entries (Mike, 19.10.) */}
      <span className="pr-gap-xs text-xs font-black tracking-wider text-text-secondary uppercase">
        {uiText().allDay}
      </span>
      {items.map((item) => {
        const people = item.people === 'family' ? [] : item.people;
        const chip = cn(
          'inline-flex min-h-touch-min max-w-full items-center gap-gap-xs truncate rounded-chip px-inset-sm text-sm font-bold',
          item.pinTone ? pinTone[item.pinTone] : 'bg-surface-raised text-text-primary',
          item.dimmed && 'opacity-45',
        );
        const dot = !item.pinTone && (
          <span
            aria-hidden
            className={cn(
              'size-2.5 shrink-0 rounded-avatar',
              people[0] ? personSolid[people[0].color] : 'bg-accent-secondary',
            )}
          />
        );
        return onItemPress ? (
          <AriaButton
            key={item.id}
            onPress={() => onItemPress(item.id)}
            className={cn(
              chip,
              'cursor-pointer outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-pressed:scale-97',
            )}
          >
            {dot}
            {item.title}
          </AriaButton>
        ) : (
          <span key={item.id} className={chip}>
            {dot}
            {item.title}
          </span>
        );
      })}
    </div>
  );
}

const stateLabel = (state: AgendaState) =>
  state === 'now' ? uiText().happeningNow : state === 'next' ? uiText().upNext : undefined;

/**
 * The shared day plan: one list for the whole family, ordered by time. Time spans (school,
 * kindergarten) show start–end, past items step back, the next one stands out.
 */
export function Agenda({
  items: all,
  nowLabel,
  onItemPress,
  pinAllDay,
  pinSticky = true,
  pinTop,
  className,
}: AgendaProps) {
  const pinned = pinAllDay ? all.filter((i) => !i.start) : [];
  const items = pinAllDay ? all.filter((i) => i.start) : all;
  // The now-line sits where "now" is on the time axis (Mike, 02.10.): before the first item that
  // starts later. Running items stay above it (marked "läuft gerade"), all-day items at the top
  // never move it – before, they pulled the line to the very top.
  const nowIndex = nowLabel ? items.findIndex((i) => i.start && i.start > nowLabel) : -1;
  const list = (
    <ol className={cn('flex flex-col', !pinAllDay && className)}>
      {items.map((item, index) => {
        const family = item.people === 'family';
        const people = family ? [] : (item.people as WeekPerson[]);
        const highlight = item.state === 'now' || item.state === 'next';
        const body = (
          <>
            <span className="flex w-16 shrink-0 flex-col pt-inset-sm text-right font-bold tabular-nums leading-tight">
              <span>{item.start ?? 'ganztags'}</span>
              {item.end && <span className="text-sm font-medium text-text-muted">{item.end}</span>}
            </span>
            {/* rail */}
            <span aria-hidden className="relative flex w-4 shrink-0 justify-center">
              <span className="absolute inset-y-0 w-0.5 bg-border-subtle" />
              <span
                className={cn(
                  'relative mt-inset-sm size-3.5 rounded-avatar border-2 border-surface-canvas',
                  family
                    ? 'bg-accent-secondary'
                    : people[0]
                      ? personSolid[people[0].color]
                      : 'bg-text-muted',
                  highlight && 'ring-4 ring-accent-primary-soft',
                )}
              />
            </span>
            <span
              className={cn(
                'mb-gap-sm flex min-w-0 flex-1 flex-col gap-gap-xs rounded-card p-inset-sm text-left',
                highlight
                  ? cn(cardSurface, 'border-l-4 border-l-accent-primary')
                  : family
                    ? 'bg-accent-secondary-soft'
                    : 'bg-surface-raised',
              )}
            >
              {stateLabel(item.state) && (
                <span className="text-xs font-bold tracking-wider text-accent-primary uppercase">
                  {stateLabel(item.state)}
                  {item.when ? ` · ${item.when}` : ''}
                </span>
              )}
              <span className="flex items-center gap-gap-sm">
                <Icon icon={item.icon} size="md" className="text-text-secondary" />
                <span className="font-display text-lg font-black leading-tight">{item.title}</span>
              </span>
              <span className="flex flex-wrap items-center gap-x-gap-md gap-y-gap-xs text-sm font-semibold">
                {family ? (
                  <span className="inline-flex items-center gap-gap-xs text-accent-secondary">
                    <Icon icon={UsersThree} size="sm" weight="fill" />
                    {uiText().everyone}
                  </span>
                ) : (
                  people.map((p) => (
                    <span
                      key={p.name}
                      className={cn('inline-flex items-center gap-gap-xs', personInk[p.color])}
                    >
                      <span
                        aria-hidden
                        className={cn('size-2.5 rounded-avatar', personSolid[p.color])}
                      />
                      {p.name}
                    </span>
                  ))
                )}
              </span>
              {item.meta && (
                <span className="flex flex-col gap-gap-xs text-sm text-text-secondary">
                  {item.meta}
                </span>
              )}
              {item.details?.length ? (
                <span className="flex flex-wrap gap-x-gap-md gap-y-0 text-sm text-text-muted">
                  {item.details.map((d) => (
                    <span key={d}>{d}</span>
                  ))}
                </span>
              ) : null}
              {item.overlap && (
                <span className="self-start rounded-control border-2 border-dashed border-feedback-danger px-inset-xs text-xs font-bold text-feedback-danger">
                  {uiText().overlapWith(item.overlap)}
                </span>
              )}
            </span>
          </>
        );
        return (
          <li
            key={item.id}
            className={cn(
              'flex flex-col',
              item.state === 'past' && 'opacity-60',
              item.dimmed && 'opacity-45 saturate-50',
            )}
          >
            {item.gapBefore ? (
              <div className="flex items-center gap-gap-sm pb-gap-sm" role="presentation">
                <span className="w-16 shrink-0" />
                <span className="w-4 shrink-0" />
                <span className="rounded-chip border-2 border-dashed border-border-subtle px-inset-sm text-xs font-bold text-text-muted">
                  {uiText().freeFor(uiText().duration(item.gapBefore))}
                </span>
              </div>
            ) : null}
            {index === nowIndex && nowLabel && (
              <div className="flex items-center gap-gap-sm py-gap-xs" role="presentation">
                <span className="w-16 text-right text-sm font-black text-accent-primary tabular-nums">
                  {nowLabel}
                </span>
                <span aria-hidden className="h-0.5 flex-1 rounded-chip bg-accent-primary" />
                <span className="text-xs font-bold tracking-wider text-accent-primary uppercase">
                  {uiText().now}
                </span>
              </div>
            )}
            {onItemPress ? (
              <AriaButton
                onPress={() => onItemPress(item.id)}
                aria-label={`${item.start ?? 'ganztags'}${item.end ? ` bis ${item.end}` : ''}, ${item.title}`}
                className="flex cursor-pointer gap-gap-sm rounded-card outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-pressed:scale-99"
              >
                {body}
              </AriaButton>
            ) : (
              <div className="flex gap-gap-sm">{body}</div>
            )}
          </li>
        );
      })}
      {nowIndex === -1 && nowLabel && items.length > 0 && (
        // Nothing starts later today: the now-line closes the day.
        <li className="flex items-center gap-gap-sm py-gap-xs" aria-hidden>
          <span className="w-16 text-right text-sm font-black text-accent-primary tabular-nums">
            {nowLabel}
          </span>
          <span className="h-0.5 flex-1 rounded-chip bg-accent-primary" />
          <span className="text-xs font-bold tracking-wider text-accent-primary uppercase">
            {uiText().now}
          </span>
        </li>
      )}
    </ol>
  );
  if (!pinAllDay) return list;
  return (
    <div className={cn('flex flex-col gap-gap-md', className)}>
      {/* sticks below the app header (Shell sets --shh-header-h) while the day scrolls */}
      {pinned.length > 0 && (
        <PinnedRow
          items={pinned}
          onItemPress={onItemPress}
          className={cn(
            pinSticky &&
              'sticky top-(--shh-header-h) z-10 -mx-inset-xs bg-surface-canvas px-inset-xs py-gap-xs',
          )}
          style={pinSticky && pinTop ? { top: pinTop } : undefined}
        />
      )}
      {list}
    </div>
  );
}
